/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import Dialog, { DialogProps } from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Grid2 from '@mui/material/Grid2'; 
import IconButton from '@mui/material/IconButton';
import { ReactComponent as More } from '../../../assets/SVG/close-circle-greay.svg';
import { getTodayDefaultDate, RequiredLabel } from '../../../utils/commonUtils';
import styles from './embryoCulture.module.css';
import { Autocomplete, Box, FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, ThemeProvider, Typography } from '@mui/material';
import { theme } from '../../../components/Medical_History_Forms/themeprovider';
import { useFormik } from 'formik';
import { cryoPurposeOptionsLocalized, fetchCanes, fetchCanisters, fetchGoblets, fetchTankData, FreezeDialogValues, freezelocationDetails, freezeValidationSchema, gradientData, initialValuesCryo, StorageUnit } from './embryoCulture.helpers';
import { getUsersByClinicId } from '../../../services/apiService';
import { useClinic } from '../../../components/ClinicProvider';
import { mediaItems } from '../Occyte-Grading/oocyteGrading.helper';
import TankSelect from './TankSelect';
import CanisterSelect from './CanisterSelect';
import CaneSelect from './CaneSelect';
import GobletSelect from './GobletSelect';
import CryotechSelect from './cryotechSelect';
import { useTranslation } from 'react-i18next';

interface FreezeActionDialogProps {
  open: boolean; 
  onClose: () => void;
  values: FreezeDialogValues;
  onSave: (formValues: FreezeDialogValues, selectedfreezedEmbryosDetails: freezelocationDetails[]) => void;
  selectedEmbryos: number[];
  specimen?:string;
  existingFreezeLocations?: freezelocationDetails[];
  defaultCryoPreservedDate?: string;
}

// Define a constant for roles to avoid magic strings
const ROLES = ['DOCTOR', 'EMBRYOLOGIST', 'NURSE'] as const;
type Role = typeof ROLES[number];
interface Clinician {
  id: number; 
  first_name: string;
  last_name: string;
  role: Role;  
}


const FreezeActionDialog: React.FC<FreezeActionDialogProps> = ({ open, onClose, values, onSave, selectedEmbryos, specimen, existingFreezeLocations, defaultCryoPreservedDate }) => {
  const { t: tGeneral, i18n } = useTranslation('general');
  const { t: tCulture } = useTranslation('culture_config'); 
  const [freezeValues, setFreezeValues] = useState<FreezeDialogValues>(values);
  const [embryoTofreeze, SetembryoTofreeze]= useState<number[]>([]);
  const [selectedfreezedEmbryos, setSelectedfreezedEmbryos] = useState<number | null>(null);;
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [selectedfreezedEmbryosDetails, setselectedfreezedEmbryosDetails] = useState<freezelocationDetails[]>([]);
  const formik = useFormik({
    initialValues: {
      ...freezeValues,
      cryo_preserved_date: defaultCryoPreservedDate || freezeValues.cryo_preserved_date || getTodayDefaultDate(),
    },
    validationSchema: freezeValidationSchema,
    onSubmit: (formValues) => {
      onSave(formValues, selectedfreezedEmbryosDetails);
      setselectedfreezedEmbryosDetails([]);
    },
    enableReinitialize: true,
  });
  
  useEffect(() => {
    SetembryoTofreeze(selectedEmbryos);
    setSelectedfreezedEmbryos(selectedEmbryos[selectedIndex]);
  }, [selectedEmbryos, selectedIndex]);

  // Initialize with existing freeze locations when dialog opens, reset when it closes
  useEffect(() => {
    if (open) {
      if (existingFreezeLocations && existingFreezeLocations.length > 0) {
        setselectedfreezedEmbryosDetails(existingFreezeLocations);
      }
    } else {
      // Reset state when dialog closes to avoid stale data
      setselectedfreezedEmbryosDetails([]);
    }
  }, [open, existingFreezeLocations]);

  // Update cryo_preserved_date when defaultCryoPreservedDate changes (e.g., denuded date updates)
  // Only applies when defaultCryoPreservedDate is provided - other components without this prop are unaffected
  useEffect(() => {
    if (open && defaultCryoPreservedDate) {
      formik.setFieldValue('cryo_preserved_date', defaultCryoPreservedDate);
    }
    // eslint-disable-next-line
  }, [open, defaultCryoPreservedDate]);

  const { ClinicId } = useClinic();
   
  const [clinicianLists, setClinicianLists] = useState<Record<Role, Clinician[]>>({
    DOCTOR: [],
    EMBRYOLOGIST: [],
    NURSE: [],
  });

  const [tankStoragePlan, setTankStoragePlan]= useState<StorageUnit[]>([]);
  const [selectedTank, setSelectedTank] = useState<number | null>(null);

  const [canisters, setCanisters] = useState<StorageUnit[]>([]);
  const [selectedCanister, setSelectedCanister] = useState<number | null>(null);
  const [loadingCanisters, setLoadingCanisters] = useState(false);

  const [canes, setCanes] = useState<StorageUnit[]>([]); 
  const [selectedCane, setSelectedCane] = useState<number | null>(null);
  const [loadingCanes, setLoadingCanes] = useState(false);

  const [goblets, setGoblets] = useState<StorageUnit[]>([]);
  const [selectedGoblet, setSelectedGoblet] = useState<number | null>(null);
  const [loadingGoblets, setLoadingGoblets] = useState(false);

  
  const [cryotechs, setCryotechs] = useState<StorageUnit[]>([]);
  const [selectedCryotech, setSelectedCryotech] = useState<number | null>(null);
  const [loadingCryotechs, setLoadingCryotechs] = useState(false);

  const [selectedGobletColor, setSelectedGobletColor] = useState<string | null>(null);
  const [isGobletColorDisabled, setIsGobletColorDisabled] = useState(false);

  const [selectedCryotechColor, setSelectedCryotechColor] = useState<string | null>(null);
  const [selectedCryotechObj, setSelectedCryotechObj] = useState<StorageUnit | null>(null);

  // const [isCryotechColorDisabled, setIsCryotechColorDisabled] = useState(false);
  
  const onClosebtn: DialogProps['onClose'] = (event, reason) => {
    if (reason !== 'backdropClick') {
      setSelectedfreezedEmbryos(selectedEmbryos[0]);
      setSelectedIndex(0);
      setselectedfreezedEmbryosDetails([]);
      setSelectedTank(null);
      setTankStoragePlan([]);
      setSelectedCanister(null);
      setCanisters([]);
      setSelectedCane(null);
      setCanes([]);
      setSelectedGoblet(null);
      setGoblets([]);
      setSelectedGobletColor(null);
      setSelectedCryotech(null);
      setCryotechs([]);
      setSelectedCryotechColor(null);
      onClose();
    }
  };



  const fetchCliniciansByRole = useCallback((role: Role) => {
    const query = `clinic=${ClinicId}&role=${role}`;
    getUsersByClinicId(query)
      .then((res) => {        
        setClinicianLists(prev => ({
          ...prev,
          [role]: res?.objects ?? [],
        }));
      })
      .catch((error) => {
        console.error(`Error fetching ${role.toLowerCase()}s:`, error);
      })
      .finally(() => {
      });
  }, [ClinicId]); 
    
  useEffect(() => {
    if (open) {
      ROLES.forEach(fetchCliniciansByRole); 
      setFreezeValues(initialValuesCryo);
    }
  }, [open, fetchCliniciansByRole]);


  const loadTankStoragePlan = useCallback(() => {
    fetchTankData(Number(ClinicId), specimen?specimen:'Embryo')
      .then((res) => {
        let tanks = res.objects || [];
  
        tanks = tanks.map(tank => {
          const cryotechsInTank = selectedfreezedEmbryosDetails.filter(embryo =>
            embryo.hierarchy?.includes(`${tank.root_name}`)
          );
  
          const updatedUnoccupiedCount = Math.max(
            (tank.unoccupied_count ?? 0) - cryotechsInTank.length,
            0
          );
  
          const occupancy = tank.total_count
            ? ((tank.total_count - updatedUnoccupiedCount) * 100) / tank.total_count
            : 0;
  
          return {
            ...tank,
            unoccupied_count: updatedUnoccupiedCount,
            occupancy: occupancy,
            is_occupied:tank.total_count && updatedUnoccupiedCount < tank.total_count
          };
        });
  
        setTankStoragePlan(tanks);
      })
      .catch((error) => {
        console.error('Error fetching tank storage plan:', error);
      });
  }, [ClinicId, selectedfreezedEmbryosDetails, specimen]);
  

  useEffect(() => {
    loadTankStoragePlan();
  }, [loadTankStoragePlan]);
 

  const loadCanisters = useCallback((tankId: number, selectedOccupancy?:number) => {
    setLoadingCanisters(true);
  
    fetchCanisters(tankId, 1)
      .then((response) => {
        
        const updatedCanisters = response.map((canister: StorageUnit) => {
          const cryotechsInCanister = selectedfreezedEmbryosDetails.filter(embryo =>            
            embryo.hierarchy?.includes(`${canister.hierarchy}`)
          );
          
          const updatedUnoccupiedCount = Math.max(
            (canister.unoccupied_count ?? 0) - cryotechsInCanister.length,
            0
          );
  
          const occupancy = canister.total_count
            ? ((canister.total_count - updatedUnoccupiedCount) * 100) / canister.total_count
            : 0;
  
          return {
            ...canister,
            unoccupied_count: updatedUnoccupiedCount,
            occupancy:selectedOccupancy?selectedOccupancy: occupancy,
            is_occupied:canister.total_count&& updatedUnoccupiedCount < canister.total_count
          };
        });
  
        setCanisters(updatedCanisters);
      })
      .catch((error) => {
        console.error('Error fetching canisters:', error);
      })
      .finally(() => {
        setLoadingCanisters(false);
      });
  }, [selectedfreezedEmbryosDetails]);
  


  const loadCanes = useCallback((tankId: number, canisterId?: number, Selectedoccupancy?:number) => {
    setLoadingCanes(true);
  
    fetchCanes(tankId, 2, canisterId ?? 0)
      .then((response) => {
  
        const updatedCanes = response.map((cane: StorageUnit) => {
          const cryotechsInCane = selectedfreezedEmbryosDetails.filter(embryo =>
            embryo.hierarchy?.includes(`${cane.hierarchy}`)
          );
  
          const updatedUnoccupiedCount = Math.max(
            (cane.unoccupied_count ?? 0) - cryotechsInCane.length,
            0
          );
  
          const occupancy = cane.total_count
            ? ((cane.total_count - updatedUnoccupiedCount) * 100) / cane.total_count
            : 0;
  
          return {
            ...cane,
            unoccupied_count: updatedUnoccupiedCount,
            occupancy: Selectedoccupancy?Selectedoccupancy:occupancy,
            is_occupied: cane.total_count &&updatedUnoccupiedCount < cane.total_count
          };
        });
  
        const sortedCanes = updatedCanes.sort((a, b) => {
          const numA = parseInt(a.name?.replace(/\D/g, '') || '0');
          const numB = parseInt(b.name?.replace(/\D/g, '') || '0');
          return numA - numB;
        });
  
        setCanes(sortedCanes);
      })
      .catch((error) => {
        console.error('Error fetching canes:', error);
      })
      .finally(() => {
        setLoadingCanes(false);
      });
  }, [selectedfreezedEmbryosDetails]);
  

  const loadGoblets = useCallback((tankId: number, caneId: number, Selectedoccupancy?: number) => {
    setLoadingGoblets(true);
  
    fetchGoblets(tankId, 3, caneId)
      .then((response) => {
  
        const updatedGoblets = response.map((goblet: StorageUnit) => {
          const cryotechsInGoblet = selectedfreezedEmbryosDetails.filter(embryo =>
            embryo.hierarchy?.includes(`${goblet.hierarchy}`)
          );
          
          const updatedUnoccupiedCount = Math.max(
            (goblet.unoccupied_count ?? 0) - cryotechsInGoblet.length,
            0
          );
          
          const occupancy = goblet.total_count
            ? ((goblet.total_count - updatedUnoccupiedCount) * 100) / goblet.total_count
            : 0;
          
          return {
            ...goblet,
            unoccupied_count: updatedUnoccupiedCount,
            occupancy:Selectedoccupancy?Selectedoccupancy:occupancy,
            is_occupied: goblet.total_count && updatedUnoccupiedCount < goblet.total_count
          };
        });
  
        const sortedGoblets = updatedGoblets.sort((a, b) => {
          const numA = parseInt(a.name?.replace(/\D/g, '') || '0');
          const numB = parseInt(b.name?.replace(/\D/g, '') || '0');
          return numA - numB;
        });
  
        setGoblets(sortedGoblets);
      })
      .catch((error) => {
        console.error('Error fetching goblets:', error);
      })
      .finally(() => {
        setLoadingGoblets(false);
      });
  }, [selectedfreezedEmbryosDetails]);
  
  
  const loadCryotechs = useCallback((tankId: number, gobletId: number, selectedCryotech?: number) => {
    setLoadingCryotechs(true);
  
    fetchGoblets(tankId, 4, gobletId)
      .then((response) => {
        
        const updatedCryotechs = response.map((cryotech: StorageUnit) => {
          const match = selectedfreezedEmbryosDetails.find(
            embryo => embryo.hierarchy?.includes(`${cryotech.hierarchy}`)
          );
          let selectedCryotechObject: StorageUnit | null = null; 
          if (cryotech.id === selectedCryotech) {
            selectedCryotechObject = cryotech;
          }
          const updatedUnoccupiedCount = match
            ? Math.max((cryotech.unoccupied_count ?? 0) - 1, 0)
            : cryotech.unoccupied_count;
          
          const occupancy = match ? 100 :cryotech.occupancy;
          return {
            ...cryotech,
            unoccupied_count: updatedUnoccupiedCount,
            occupancy:selectedCryotechObject?100:occupancy,
            is_occupied: cryotech.total_count && updatedUnoccupiedCount && updatedUnoccupiedCount < cryotech.total_count
          };
        });
  
        const sortedCryotechs = updatedCryotechs.sort((a, b) => {
          const numA = parseInt(a.name?.replace(/\D/g, '') || '0');
          const numB = parseInt(b.name?.replace(/\D/g, '') || '0');
          return numA - numB;
        });
  
        setCryotechs(sortedCryotechs);
      })
      .catch((error) => {
        console.error('Error fetching cryotechs:', error);
      })
      .finally(() => {
        setLoadingCryotechs(false);
      });
  }, [selectedfreezedEmbryosDetails]);
  
  
  const handleTankChange = (value: string) => {
    const newTankId = Number(value) || null;
    setSelectedTank(newTankId);

    formik.setFieldValue('tank', newTankId);
    formik.setFieldTouched('tank', false, false);
    if (newTankId) {
      loadCanisters(newTankId);
      setCanisters([]);
      setCanes([]); 
      setSelectedCanister(null);
      setSelectedCane(null);
      setSelectedGoblet(null);
      setCryotechs([]);
      setSelectedCryotech(null);

      formik.setFieldValue('canister', null);
    } else {
      setCanisters([]);
      setCanes([]);
    }
  };
  
  const handleCanisterChange = (value: string) => {
    const canisterId = Number(value);
    setSelectedCanister(canisterId);

    formik.setFieldValue('canister', canisterId);
    formik.setFieldTouched('canister', false, false);

    if (canisterId && selectedTank) {
      loadCanes(selectedTank, canisterId);
      setSelectedCane(null); 
      setSelectedGoblet(null);
      setSelectedCryotech(null);
      formik.setFieldValue('cane', null);
    }
  };


  const handleCaneChange = (value: string) => {
    const caneId = Number(value);
    setSelectedCane(caneId);
  
    formik.setFieldValue('cane', caneId);
    formik.setFieldTouched('cane', false, false);

    if (caneId && selectedTank) {
      loadGoblets(selectedTank, caneId);
      setSelectedGoblet(null); 
      setSelectedCryotech(null);
      formik.setFieldValue('goblet', null);
    }
  };
  

  const handleGobletChange = (value: string) => {
    const gobletId = Number(value);

    const selectedGobletObj = goblets.find((g) => g.id === gobletId);
    
    
    setSelectedGoblet(gobletId);
  
    formik.setFieldValue('goblet', gobletId);
    formik.setFieldTouched('goblet', false, false);

    if(selectedGobletObj){
      setSelectedGobletColor(selectedGobletObj.color || null);
      setIsGobletColorDisabled(!!selectedGobletObj.color);

      formik.setFieldValue('goblet_color', selectedGobletObj.color);
      formik.setFieldTouched('goblet_color', false);
  
      formik.setFieldError('goblet_color', '');
    }
    
    if (gobletId && selectedTank) {
      loadCryotechs(selectedTank, gobletId);
      setSelectedCryotech(null);
      formik.setFieldValue('cryotech', null);      
    }
  };

  const handleCryotechChange = (value: string) => {
    const cryotechID = Number(value);
    
    formik.setFieldValue('cryotech', cryotechID);
    formik.setFieldTouched('cryotech', false, false);

    setSelectedCryotech(cryotechID);    
  };
  
  const handleGobletColorChange = (event: SelectChangeEvent<string>) => {
    const selectedColor = event.target.value;
    setSelectedGobletColor(selectedColor);
    setIsGobletColorDisabled(false);

    formik.setFieldValue('goblet_color', selectedColor);
    formik.setFieldTouched('goblet_color', false);
    
  };

  const handleCryotechColorChange = (event: SelectChangeEvent<string>) => {
    const selectedColor = event.target.value;
    setSelectedCryotechColor(selectedColor);
    // setIsCryotechColorDisabled(false);
  
    // Update Formik's field value for cryotech_color and clear touched state
    formik.setFieldValue('cryotech_color', selectedColor);
    formik.setFieldTouched('cryotech_color', false);
  
  };
  
  const handlenext=()=>{
    const selectedEmbdetail: freezelocationDetails= {
      cryotech:selectedCryotech,
      cryotech_color:selectedCryotechColor,
      goblet:selectedGoblet,
      goblet_color:selectedGobletColor,
      embryo_number:selectedfreezedEmbryos ?? 0,
      hierarchy:selectedCryotechObj?.hierarchy,
      parent:selectedCryotechObj?.parent,
      plan:selectedCryotechObj?.plan
    };

    // setselectedfreezedEmbryosDetails((prev) => [...prev, selectedEmbdetail]);

    
    setselectedfreezedEmbryosDetails((prev) => {
      const existingIndex = prev.findIndex(
        (item) => item.embryo_number === selectedEmbdetail.embryo_number
      );

      if (existingIndex !== -1) {
        // Replace the existing entry
        const updated = [...prev];
        updated[existingIndex] = selectedEmbdetail;
        return updated;
      } else {
        // Add new entry
        return [...prev, selectedEmbdetail];
      }
    });


    if (selectedIndex <= embryoTofreeze.length) {
      setSelectedIndex((prev) => prev + 1);
    }
      
 
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const tank:any = tankStoragePlan.find(t => t.id === selectedTank);

    if (tank && selectedTank !== null) {
      tank.occupancy = ((tank.total_count - (tank.unoccupied_count - 1)) * 100) / tank.total_count;
    
      if (Math.round(tank.occupancy) === 100) {
        setSelectedTank(null);
        setTankStoragePlan([]);
        setSelectedCanister(null);
        setCanisters([]);
        setSelectedCane(null);
        setCanes([]);
        setSelectedGoblet(null);
        setGoblets([]);
        setSelectedGobletColor(null);
        setSelectedCryotech(null);
        setCryotechs([]);
        setSelectedCryotechColor(null);
        return;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const canister:any = canisters.find(c => c.id === selectedCanister);
    
    if (canister && selectedCanister !== null) {
      const updatedOccupancy = ((canister.total_count - (canister.unoccupied_count - 1)) * 100) / canister.total_count;
      canister.occupancy = updatedOccupancy;
      setCanisters(prevCanisters => 
        prevCanisters.map(c => 
          c.id === selectedCanister ? { ...c, occupancy: updatedOccupancy } : c
        )
      );
      if (Math.round(updatedOccupancy) === 100) {
        setSelectedCanister(null);
        setCanisters([]);
        setSelectedCane(null);
        setCanes([]);
        setSelectedGoblet(null);
        setGoblets([]);
        setSelectedGobletColor(null);
        setSelectedCryotech(null);
        setCryotechs([]);
        setSelectedCryotechColor(null);
        loadCanisters(selectedTank ? selectedTank:0, 100);
        return;
      }
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cane: any = canes.find(c => c.id === selectedCane);

    if (cane && selectedCane !== null) {
      const updatedOccupancy = ((cane.total_count - (cane.unoccupied_count - 1)) * 100) / cane.total_count;
          
      setCanes(prevCanes =>
        prevCanes.map(c =>
          c.id === selectedCane ? { ...c, occupancy: updatedOccupancy } : c
        )
      );
        
      if (Math.round(updatedOccupancy) === 100) {
        setSelectedCane(null);
        setCanes([]);
        setSelectedGoblet(null);
        setGoblets([]);
        setSelectedGobletColor(null);
        setSelectedCryotech(null);
        setCryotechs([]);
        setSelectedCryotechColor(null);
        loadCanes(selectedTank ? selectedTank : 0,selectedCanister?selectedCanister:0, 100);
        return;
      }
    }

 

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const goblet: any = goblets.find(g => g.id === selectedGoblet);

    if (goblet && selectedGoblet !== null) {
      const updatedOccupancy = ((goblet.total_count - (goblet.unoccupied_count - 1)) * 100) / goblet.total_count;
  
      setGoblets(prevGoblets =>
        prevGoblets.map(g =>
          g.id === selectedGoblet ? { ...g, occupancy: updatedOccupancy } : g
        )
      );

      if (Math.round(updatedOccupancy) === 100) {
        setSelectedGoblet(null);
        setGoblets([]);
        setSelectedGobletColor(null);
        setSelectedCryotech(null);
        setCryotechs([]);
        setSelectedCryotechColor(null);
        loadGoblets(selectedTank ? selectedTank : 0,selectedCane?selectedCane:0, 100);
        return;
      }
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cryotech: any = cryotechs.find(c => c.id === selectedCryotech);

    if (cryotech && selectedCryotech !== null) {
      const updatedOccupancy = 100;
  
      setCryotechs(prevCryotechs =>
        prevCryotechs.map(c =>
          c.id === selectedCryotech ? { ...c, occupancy: 100 } : c
        )
      );

      if (Math.round(updatedOccupancy) === 100) {
        loadCryotechs(selectedTank ? selectedTank : 0,selectedGoblet?selectedGoblet:0,selectedCryotech);
        setSelectedCryotech(null);
        setCryotechs([]);
        setSelectedCryotechColor(null);
        return;
      }
    }


  };

  const handleCryoChnge=(value: StorageUnit |null)=>{
    setSelectedCryotechObj(value);
  };

  // Gradient color mapping for circles
  const gradientMap: Record<string, string> = {
    White: 'radial-gradient(circle, #FFFFFF 0%, #DCDCDC 100%)',
    Blue: 'radial-gradient(circle, #5C8CDB 0%, #BDD6FF 80%)',
    Brown: 'radial-gradient(circle, #8B5A2B 0%, #F5E6DA 100%)',
    Green: 'radial-gradient(circle, #4CAF50 0%, #DFF5E0 100%)',
    Orange: 'radial-gradient(circle, #E78457 0%, #FFDCCC 100%)',
    Red: 'radial-gradient(circle, #E74C3C 0%, #FFE5E0 100%)',
    Yellow: 'radial-gradient(circle, #DEC122 0%, #FFFCE9 100%)',
  };
    
  // Function to render a colored circle based on gradient map
  const GradientCircle: React.FC<{ color?: string }> = ({ color }) => {
    if (!color || !gradientMap[color]) return null;
    
    return (
      <span
        style={{
          width: '15px',
          height: '15px',
          borderRadius: '50%',
          background: gradientMap[color],
          marginLeft: '2px',
          marginRight: '6px',
          boxShadow: 'inset 1px 1px 3px rgba(0,0,0,0.2), 0 3px 6px rgba(0,0,0,0.1)',
          border: '1px solid rgba(255,255,255,0.7)',
          display: 'inline-block',
          verticalAlign: 'middle',
        }}
      ></span>
    );
  };
  
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const formatLocationDetails = (locationDetails: any): JSX.Element => {

    if (!locationDetails) {
      return <span>{tGeneral('notAvailable', { defaultValue: 'N/A' })}</span>;
    }
     
    const hierarchyParts = locationDetails.hierarchy.split('->').map((part: string) => part.trim());
    const gobletPart = hierarchyParts.find((part: string) => part.startsWith('Goblet ')  || part.startsWith('Gob') || part.startsWith('gob'));
    const cryotechPart = hierarchyParts.find((part: string) => part.startsWith('cryotech')|| part.startsWith('cryo')|| part.startsWith('Cryotech'));
    const hierarchy= hierarchyParts.filter((p: string) => !p.startsWith('Goblet') && !p.startsWith('cryotech') && !p.startsWith('cryo')&& !p.startsWith('Cryotech') && !p.startsWith('Gob') && !p.startsWith('gob')).join(' | ');
    const goblet = {
      color: locationDetails.goblet_color,
      name: gobletPart || ''
    };

    const cryotec = {
      color: locationDetails.cryotech_color,
      name: cryotechPart || ''
    };

    
       
    return (
      <span className='d-flex'>
        <Typography sx={{ fontSize: '16px' }}>

          {hierarchy}
        </Typography>
        {goblet && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', ml: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontSize: '16px', mr: 1 }}>
                 | {goblet.name}
            </Typography>
            <GradientCircle color={goblet.color || ''} />
          </Box>
        )}
        {cryotec && (
          <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', whiteSpace: 'nowrap', ml: 1 }}>
            <Typography variant="body2" component="span" sx={{ fontSize: '16px', mr: 1 }}>
                 | {cryotec.name}
            </Typography>
            <GradientCircle color={cryotec.color || ''} />
          </Box>
        )}
      </span>
    );
  };
  return (    
    <ThemeProvider theme={theme}>
      <Dialog open={open} onClose={onClosebtn}  hideBackdrop keepMounted   disableEscapeKeyDown={false} 
        fullWidth maxWidth='lg'
        PaperProps={{  style: {
          pointerEvents: 'auto',
        },sx: {  borderRadius: '12px', width: '55%',  minWidth: '700px',},  }}>
        <DialogTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', gap: '1px' }}>
            <div style={{ display: 'flex', gap: '8px' }}>{tGeneral('action', { defaultValue: 'Action' })}
              <span style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                {embryoTofreeze.map((num, index) => {
                  const isSelected = selectedfreezedEmbryos === num;
                  return (
                    <span
                      key={num}
                      onClick={() => setSelectedIndex(index)}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '50%',
                        backgroundColor: isSelected ? '#0D99FF' : '#47B35F',
                        color: 'white',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        fontSize: '12px',
                        fontWeight: 400,
                        cursor: 'pointer',
                        border: isSelected ? '2px solid #005ea2' : 'none',
                      }}
                    >
                      {num}
                    </span>
                  );
                })}
              </span>

              {/* <button
                onClick={() => {handlenext();}}
              >
  Next
              </button> */}
            </div>

            <IconButton
              edge='end'
              color="inherit"
              onClick={()=>onClosebtn('','escapeKeyDown')}
              aria-label="close"
            >
              <More />
            </IconButton>
          </div>
        </DialogTitle>


        <form onSubmit={formik.handleSubmit}>
          <DialogContent>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                <TextField
                  name="cryo_preserved_date"
                  type="date"
                  label={RequiredLabel(tCulture('cryoPreservedDate', { defaultValue: 'Cryo Preserved Date' }), true)}
                  fullWidth
                  value={formik.values.cryo_preserved_date || getTodayDefaultDate()}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.cryo_preserved_date && Boolean(formik.errors.cryo_preserved_date)}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid2>
              <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  id="cryo_preserved_by"
                  options={clinicianLists['DOCTOR'] || []}
                  getOptionLabel={(option) => (option.first_name || '') + ' ' + (option.last_name || '')}
                  value={
                    clinicianLists['DOCTOR'].find(
                      (doctor) => doctor.id === formik.values.cryo_preserved_by // Use formik.values directly here
                    ) || null
                  }
                  onChange={(_, value) => {
                    // Manually update Formik's field value with the selected doctor's id
                    formik.setFieldValue('cryo_preserved_by', value?.id || '');  // Update with the clinician's ID
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(tCulture('cryoPreservedBy', { defaultValue: 'Cryo Preserved By' }), true)}

                      error={formik.touched.cryo_preserved_by && Boolean(formik.errors.cryo_preserved_by)}
                    />
                  )}
                />
              </Grid2>

                     
              <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  id="cryo_purpose"
                  options={cryoPurposeOptionsLocalized}
                  getOptionLabel={(option) => {
                    // Robust fallback: try i18n.language, then 'en', then any available string, then code
                    const desc = option.description || {};
                    if (typeof desc === 'string') return desc;
                    if ((desc as Record<string, string>)[i18n.language]) return (desc as Record<string, string>)[i18n.language];
                    if (desc.en) return desc.en;
                    // Try any string value in description
                    const anyLang = Object.values(desc).find(val => typeof val === 'string' && val.trim() !== '');
                    if (anyLang) return anyLang;
                    // Fallback to code or empty string
                    return option.code || '';
                  }}
                  value={cryoPurposeOptionsLocalized.find(option => option.code === formik.values.cryo_purpose) || null}
                  onChange={(_, value) => {
                    formik.setFieldValue('cryo_purpose', value?.code || '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={RequiredLabel(
                        tCulture('cryoPurpose', { defaultValue: 'Cryo Purpose' }),
                        true
                      )}
                      error={formik.touched.cryo_purpose && Boolean(formik.errors.cryo_purpose)}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                <Autocomplete
                  fullWidth
                  size="small"
                  id="cryo_media"
                  options={mediaItems}  
                  getOptionLabel={(option) => tGeneral(option.label)}                  
                  value={mediaItems.find(option => option.value === formik.values.cryo_media) || null}
                  onChange={(_, value) => {
                    formik.setFieldValue('cryo_media', value?.value || '');  
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={tCulture('media', { defaultValue: 'Media' })}
                      error={formik.touched.cryo_media && Boolean(formik.errors.cryo_media)}
                    />
                  )}
                />
              </Grid2> 

              <Grid2 size={{ xs: 4, sm: 4, md: 4 }}>
                <TextField
                  select
                  fullWidth
                  size="small"
                  id="is_screened"
                  label={tCulture('screeningStatus', { defaultValue: 'Screening Status' })}
                  value={formik.values.is_screened === true ? true : formik.values.is_screened === false ? false : ''}
                  onChange={(e) => {
                    formik.setFieldValue('is_screened', e.target.value === 'true' ? true : false);
                  }}
                  error={formik.touched.is_screened && Boolean(formik.errors.is_screened)}
                  helperText={formik.touched.is_screened && formik.errors.is_screened}
                >
                  <MenuItem value="true">{tGeneral('yes', { defaultValue: 'Yes' })}</MenuItem>
                  <MenuItem value="false">{tGeneral('no', { defaultValue: 'No' })}</MenuItem>
                </TextField>
              </Grid2>
            </Grid2>
              

            <Grid2 container spacing={2} mt={3}>
              <Grid2 size={{ xs: 12}}>
                <Typography variant="subtitle1" component="div">
                  {tCulture('location', { defaultValue: 'Location' })}
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <TankSelect
                  tanks={tankStoragePlan}
                  selectedTank={selectedTank}
                  onChange={handleTankChange}
                  label={tCulture('tank', { defaultValue: 'Tank' })}
                  hasError={formik.touched.tank && Boolean(formik.errors.tank)}
                />
              </Grid2>


              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <CanisterSelect
                  canisters={canisters}
                  selectedCanister={selectedCanister}
                  onChange={handleCanisterChange}
                  label={tCulture('canister', { defaultValue: 'Canister' })}
                  loading={loadingCanisters}
                  hasError={formik.touched.canister && Boolean(formik.errors.canister)}
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <CaneSelect
                  canes={canes}
                  selectedCane={selectedCane}
                  onChange={handleCaneChange}
                  label={tCulture('cane', { defaultValue: 'Cane' })}
                  loading={loadingCanes}
                  hasError={formik.touched.cane && Boolean(formik.errors.cane)}
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <GobletSelect
                  goblets={goblets}
                  selectedGoblet={selectedGoblet}
                  onChange={handleGobletChange}
                  label={tCulture('goblet', { defaultValue: 'Goblet' })}
                  loading={loadingGoblets}
                  hasError={formik.touched.goblet && Boolean(formik.errors.goblet)}
                />
              </Grid2>


              <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <FormControl fullWidth variant="outlined" error={formik.touched.goblet_color && Boolean(formik.errors.goblet_color)}>
                  <InputLabel id="gradient-select-label">{RequiredLabel(tCulture('gobletColor', { defaultValue: 'Goblet Color' }), true)}</InputLabel>
                  <Select labelId="gradient-select-label" label={tCulture('gobletColor', { defaultValue: 'Goblet Color' })} 
                    value={selectedGobletColor || ''} 
                    disabled={isGobletColorDisabled}                    
                    onChange={handleGobletColorChange}
                    sx={{
                      height: '52px',
                      '& .MuiSelect-select': { 
                        display: 'flex',
                        alignItems: 'center', 
                      },
                    }}>
                    {gradientData.gradients.map((gradient) => (
                      <MenuItem key={gradient.name} value={gradient.name} sx={{ height: '48px' }}>                        
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <span
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              background: gradient.background,
                              marginRight: '12px',
                              boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
                            }}
                          ></span>
                          <Typography
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 700,
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center', // Vertically center the text
                            }}
                          >
                            {gradient.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>
              
              <Grid2 size={{ xs: 6, sm: 6, md: 6 }}>
                <CryotechSelect
                  cryotechs={cryotechs}
                  selectedCryotech={selectedCryotech}
                  onChange={handleCryotechChange}
                  selectedCryotechObj={handleCryoChnge}
                  label={tCulture('cryotech', { defaultValue: 'Cryotech' })}
                  loading={loadingCryotechs}
                  hasError={formik.touched.cryotech && Boolean(formik.errors.cryotech)}
                />
              </Grid2>

              <Grid2 size={{ xs: 6, sm: 6, md: 6, lg: 6 }}>
                <FormControl fullWidth variant="outlined" error={formik.touched.cryotech_color && Boolean(formik.errors.cryotech_color)}>
                  <InputLabel id="gradient-select-label">{RequiredLabel(tCulture('cryotechColor', { defaultValue: 'Cryotech Color' }), true)}</InputLabel>
                  <Select
                    labelId="gradient-select-label"
                    label={RequiredLabel(tCulture('cryotechColor', { defaultValue: 'Cryotech Color' }), true)}        
                    value={selectedCryotechColor || ''}                                         
                    onChange={handleCryotechColorChange}
                    sx={{
                      height: '52px',
                      '& .MuiSelect-select': {
                        display: 'flex',
                        alignItems: 'center', 
                      },
                    }}
                  >
                    {gradientData.gradients.map((gradient) => (
                      <MenuItem key={gradient.name} value={gradient.name} sx={{ height: '48px' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                          <span
                            style={{
                              width: '16px',
                              height: '16px',
                              borderRadius: '50%',
                              background: gradient.background,
                              marginRight: '12px',
                              boxShadow: '0 3px 6px rgba(0, 0, 0, 0.15)',
                            }}
                          ></span>
                          <Typography
                            sx={{
                              fontFamily: 'Montserrat, sans-serif',
                              fontWeight: 700,
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center', 
                            }}
                          >
                            {gradient.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid2>

            </Grid2>

            {selectedfreezedEmbryosDetails.length >0 &&(

              <Grid2 container mt={3}>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead> 
                      <TableRow>
                        <TableCell><strong>{specimen} {tCulture('number', { defaultValue: 'No.' })}</strong></TableCell>
                        <TableCell><strong>{ tCulture('location', { defaultValue: 'Location' })}</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedfreezedEmbryosDetails.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>
                            {specimen && specimen.toLowerCase() === 'oocyte' ? `O_${item.embryo_number}` : `E_${item.embryo_number}`}
                          </TableCell>
                          <TableCell>{formatLocationDetails(item)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid2>
            )}
          </DialogContent>
          <DialogActions>
            <button type="button" onClick={()=>onClosebtn('','escapeKeyDown')} className={styles.resetButton}>
              {tGeneral('Cancel', { defaultValue: 'Cancel' })}
            </button>
            {embryoTofreeze && (
              selectedIndex === (embryoTofreeze.length)   ? (
                <button type="submit" className={styles.customButton}>
                  <strong>{tGeneral('save', { defaultValue: 'Save' })}</strong>
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handlenext}
                  className={`${styles.customButton} ${(
                    !selectedTank ||
                    !selectedCanister ||
                    !selectedCane ||
                    !selectedGoblet ||
                    !selectedCryotech ||
                    !selectedGobletColor ||
                    !selectedCryotechColor
                  ) ? styles.disabledButton : ''}`}
                
                  disabled={
                    !selectedTank ||
                    !selectedCanister ||
                    !selectedCane ||
                    !selectedGoblet ||
                    !selectedCryotech ||
                    !selectedGobletColor ||
                    !selectedCryotechColor
                  }
                >
                  <strong>{tGeneral('next', { defaultValue: 'Next' })}</strong>
                </button>
              )
            )}

          </DialogActions>
        </form>
      </Dialog>
    </ThemeProvider>
  );
};

export default FreezeActionDialog; 

